import React, { useEffect, useState } from "react";

import AdminRoutes from "Routes/Components/Admin";
import CommonRoutes from "Routes/Components/Auth";

import AdminTheme from "Styles/Themes/admin";
import CommonTheme from "Styles/Themes/common";

import { HTTPErrorCallback } from "Helpers/Error";
import {
  clearPostLoginRedirect,
  consumePostLoginRedirect,
} from "Helpers/PostLoginRedirect";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

export const parseJwt = () => {
  if (!localStorage.getItem("userJWTToken")) return {};

  const base64Url = localStorage.getItem("userJWTToken").split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

  const jwt = JSON.parse(window.atob(base64));

  jwt.full_name = decodeURIComponent(escape(jwt.full_name));

  return jwt;
};

export const AuthContext = React.createContext({
  authorized: false,
  loadingAuth: false,
  parsedJWT: {},
  isAdmin: false,
  token: null,
  storeToken: () => {},
  refreshProfile: () => {},
  logout: () => {},
  canAccess: () => {},
  exitMirror: () => {},
});

const AuthProvider = ({ children, setTheme }) => {
  const { api } = UseServices();
  const { setLoading: setGlobalLoading } = UseGlobalLoading();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(
    localStorage.getItem("userJWTToken") || null,
  );
  const [authorized, setAuthorized] = useState(
    localStorage.getItem("userJWTToken") ? true : false,
  );

  const [parsedJWT, setParsedJWT] = useState({ user: parseJwt() });
  const [isAdmin, setIsAdmin] = useState(parseJwt().profile_type_id === 1);

  useEffect(() => {
    setParsedJWT({ user: parseJwt() });

    if (authorized) {
      refreshProfile();
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      parsedJWT &&
      parsedJWT.user &&
      parsedJWT.user.profile_type_id === 1 &&
      !localStorage.getItem("oldJWTToken")
    ) {
      setIsAdmin(true);
      setTheme(AdminTheme);
    } else setTheme(CommonTheme);
    //eslint-disable-next-line
  }, [parsedJWT]);

  const storeToken = (token, redirect) => {
    setToken(localStorage.getItem("userJWTToken"));
    localStorage.setItem("userJWTToken", token);

    if (redirect) {
      // Se o usuário tentou abrir um link direto sem sessão (ex.: link de
      // interesse do e-mail), volta para lá em vez da home.
      const target = consumePostLoginRedirect();

      window.location.href =
        target ||
        (isAdmin ? AdminRoutes.Dashboard.path : CommonRoutes.Dashboard.path);
    }
  };

  const exitMirror = () => {
    localStorage.setItem("userJWTToken", localStorage.getItem("oldJWTToken"));
    localStorage.removeItem("oldJWTToken");
    window.location.replace(AdminRoutes.ListUsers.path);
  };

  const logout = () => {
    if (localStorage.getItem("oldJWTToken")) {
      return exitMirror();
    }

    // Quem clica em "Sair" quer sair: um destino pendente de antes não pode
    // sequestrar o próximo login (D3, 24/09/2026).
    clearPostLoginRedirect();

    localStorage.removeItem("userJWTToken");
    localStorage.removeItem("oldJWTToken");

    setAuthorized(false);
  };

  const refreshProfile = () => {
    setLoading(true);
    setGlobalLoading(true);
    setParsedJWT({ user: parseJwt() });
    api
      .get("/users/get-profile")
      .then((response) => {
        setParsedJWT({ user: parseJwt(), ...response.data });
        setLoading(false);
        setGlobalLoading(false);
      })
      .catch((error) => {
        HTTPErrorCallback(error);
        logout();
        setLoading(false);
        setGlobalLoading(false);
      });
  };

  const filterRoutes = (value, parsedJWT) => {
    if (!value.permissions?.length || !parsedJWT.user?.permissions?.length)
      return true;

    const contains = parsedJWT.user?.permissions.some((element) => {
      return value.permissions.indexOf(element) !== -1;
    });

    return contains;
  };

  const canAccess = (permission, permissions) => {
    if (!permission) return;

    return permissions?.includes(permission);
  };
  const contextValue = {
    authorized,
    parsedJWT,
    isAdmin,
    refreshProfile,
    token,
    storeToken,
    logout,
    loadingAuth: loading,
    filterRoutes,
    canAccess: (permission) =>
      canAccess(permission, parsedJWT?.user?.permissions),
    exitMirror,
  };

  return (
    <>
      <AuthContext.Provider value={contextValue}>
        {!loading && children}
      </AuthContext.Provider>
    </>
  );
};

export default AuthProvider;
